/**
 * V4.24.46 — Shared building blocks for the customer/agent loan pages.
 * Mirrors mobile `_LOAN_STATUS_LABELS` and offer-status pill semantics.
 */
import React from 'react';
import { Badge } from '../../components/ui/badge';
import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const LOAN_STATUS_META = {
  draft:              { label: 'Draft',              color: 'bg-gray-100 text-gray-700' },
  profiling:          { label: 'Profiling',          color: 'bg-blue-100 text-blue-700' },
  waiting_score:      { label: 'Waiting Score',      color: 'bg-blue-100 text-blue-700' },
  ready_pick_banker:  { label: 'Ready Pick Bank',    color: 'bg-cyan-100 text-cyan-700' },
  waiting_bank:       { label: 'Waiting Bank',       color: 'bg-amber-100 text-amber-700' },
  waiting_banker:     { label: 'Waiting Bank',       color: 'bg-amber-100 text-amber-700' },
  waiting_outsource:  { label: 'Waiting Outsource',  color: 'bg-amber-100 text-amber-700' },
  bankers_available:  { label: 'Available Banker',   color: 'bg-cyan-100 text-cyan-700' },
  own_bank_waiting:   { label: 'Own Bank — Waiting', color: 'bg-amber-100 text-amber-700' },
  offer_accepted:     { label: 'Offer Accepted',     color: 'bg-emerald-100 text-emerald-700' },
  lo_meetup:          { label: 'LO Meetup',          color: 'bg-indigo-100 text-indigo-700' },
  lo_signed:          { label: 'LO Signed',          color: 'bg-violet-100 text-violet-700' },
  in_review:          { label: 'In Review',          color: 'bg-violet-100 text-violet-700' },
  special_handling:   { label: 'Special Handling',   color: 'bg-amber-100 text-amber-700' },
  approved:           { label: 'Approved',           color: 'bg-green-100 text-green-700' },
  drawdown:           { label: 'Drawn',              color: 'bg-indigo-100 text-indigo-700' },
  spa_signed:         { label: 'SPA Signed',         color: 'bg-teal-100 text-teal-700' },
  rejected:           { label: 'Rejected',           color: 'bg-red-100 text-red-700' },
  closed:             { label: 'Closed',             color: 'bg-zinc-100 text-zinc-700' },
};

export const OFFER_STATUS_META = {
  submitted:   { label: 'Submitted',    color: 'bg-slate-100 text-slate-700' },
  reviewing:   { label: 'Reviewing',    color: 'bg-amber-100 text-amber-700' },
  approved:    { label: 'Approved',     color: 'bg-emerald-100 text-emerald-700' },
  accepted:    { label: 'Accepted',     color: 'bg-emerald-100 text-emerald-700' },
  superseded:  { label: 'Superseded',   color: 'bg-slate-100 text-slate-500' },
  rejected:    { label: 'Rejected',     color: 'bg-red-100 text-red-700' },
  drawdown:    { label: 'Drawn',        color: 'bg-indigo-100 text-indigo-700' },
};

export function StatusBadge({ status, label }) {
  const meta = LOAN_STATUS_META[status] || { label: label || (status || '').replace(/_/g, ' '), color: 'bg-gray-100 text-gray-700' };
  return <Badge className={meta.color} data-testid={`loan-status-badge-${status}`}>{label || meta.label}</Badge>;
}

export function OfferStatusBadge({ status }) {
  const meta = OFFER_STATUS_META[status] || { label: (status || '').replace(/_/g, ' '), color: 'bg-slate-100 text-slate-600' };
  return <Badge variant="outline" className={meta.color}>{meta.label}</Badge>;
}

// V4.24.46 — Deep-link parity with Mobile: the same next_screen keys used
// by the mobile app map to these Web routes here so QR/email/deep links
// land on the equivalent surface regardless of platform.
//
// V4.24.77 — When status is `ready_pick_banker` and the customer has NOT
// yet picked a selection_path, the backend still emits
// `loan_recommended_banks` (mobile shows CCRIS+DSR+path picker on the
// same screen). On Web we split that into two pages: `/score` (path
// picker) and `/pick-bank` (Type C stage-2 fan-out). To avoid skipping
// the score/path picker, we route based on selection_path:
//   • no path (null/'')     → /score  (pick Type A/B/C first)
//   • path = C_recommended  → /pick-bank (Type C stage-2)
//   • path = A/B/D          → /loan/:id (their respective detail page)
export function routeForNextScreen(caseId, nextScreen, selectionPath = null) {
  switch (nextScreen) {
    case 'loan_questionnaire':      return `/loan/${caseId}/score`;
    case 'loan_type_picker':        return `/loan/${caseId}/score`;
    case 'loan_detail':             return `/loan/${caseId}`;
    case 'loan_available_bankers':  return `/loan/${caseId}/pick-outsource-bankers`;
    case 'loan_recommended_banks':
      if (selectionPath === 'C_recommended') return `/loan/${caseId}/pick-bank`;
      if (selectionPath === 'A_own_bank' || selectionPath === 'D_any_bank') return `/loan/${caseId}`;
      if (selectionPath === 'B_outsource') return `/loan/${caseId}`;
      // No selection_path yet — user must pick Type A/B/C first.
      return `/loan/${caseId}/score`;
    case 'loan_offers':             return `/loan-offers`;
    case 'loan_lo_meetup':          return `/loan/${caseId}/lo-meetup`;
    case 'loan_result':             return `/loan/${caseId}`;
    default:                        return `/loan/${caseId}`;
  }
}

export function LoanPageHeader({ caseData, title }) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-between flex-wrap gap-2 border-b pb-3 mb-4">
      <button
        onClick={() => navigate('/loan-offers')}
        className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        data-testid="loan-page-back"
      >
        <ChevronLeft className="w-4 h-4" /> Back to Loan Offers
      </button>
      <div className="flex items-center gap-2">
        {caseData?.case_ref && <span className="text-xs font-mono text-gray-500">{caseData.case_ref}</span>}
        {caseData?.status && <StatusBadge status={caseData.status} label={caseData.loan_status_label} />}
      </div>
      {title && <h1 className="w-full text-2xl font-bold text-gray-800 mt-2">{title}</h1>}
    </div>
  );
}
